
(function () {
  if (window.customElements.get('dreame-glass')) return;

  const sourceMap = new WeakMap();
  let sourceId = 0;

  function parseRadius(el, w, h) {
    const variant = el.getAttribute('variant') || 'card';
    if (variant === 'circle') return Math.min(w, h) / 2;
    const raw = getComputedStyle(el).borderRadius;
    const n = parseFloat(raw);
    if (!Number.isNaN(n) && n > 0) return Math.min(n, w / 2, h / 2);
    return variant === 'pill' ? Math.min(w, h) / 2 : Math.min(16, w / 2, h / 2);
  }

  function roundRectPath(ctx, x, y, w, h, r) {
    const rad = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    if (typeof ctx.roundRect === 'function') {
      ctx.roundRect(x, y, w, h, rad);
    } else {
      ctx.moveTo(x + rad, y);
      ctx.arcTo(x + w, y, x + w, y + h, rad);
      ctx.arcTo(x + w, y + h, x, y + h, rad);
      ctx.arcTo(x, y + h, x, y, rad);
      ctx.arcTo(x, y, x + w, y, rad);
      ctx.closePath();
    }
  }

  function renderGlassCanvas(glassEl, bgCanvas, sourceEl) {
    const canvas = glassEl._glassCanvas;
    if (!canvas || !bgCanvas) return;

    const sr = sourceEl.getBoundingClientRect();
    const pr = glassEl.getBoundingClientRect();
    const w = Math.round(pr.width);
    const h = Math.round(pr.height);
    if (w <= 0 || h <= 0) return;

    canvas.width = w;
    canvas.height = h;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';

    const ox = Math.round(pr.left - sr.left);
    const oy = Math.round(pr.top - sr.top);
    const scW = bgCanvas.width / sr.width;
    const scH = bgCanvas.height / sr.height;

    const ctx = canvas.getContext('2d');
    const bgCtx = bgCanvas.getContext('2d');

    const srcX = Math.round(ox * scW);
    const srcY = Math.round(oy * scH);
    const srcW = Math.round(w * scW);
    const srcH = Math.round(h * scH);
    const clampedSrcW = Math.min(srcW, bgCanvas.width - srcX);
    const clampedSrcH = Math.min(srcH, bgCanvas.height - srcY);

    if (clampedSrcW <= 0 || clampedSrcH <= 0) return;

    const src = bgCtx.getImageData(srcX, srcY, clampedSrcW, clampedSrcH);
    const dst = ctx.createImageData(w, h);

    const amp = parseFloat(glassEl.getAttribute('amp')) || 3.5;
    const freqX = 2.8 / w;
    const freqY = 2.2 / h;

    for (let py = 0; py < h; py++) {
      for (let px = 0; px < w; px++) {
        const dx =
          Math.sin(py * freqY * Math.PI * 2 + 0.8) * amp +
          Math.sin(px * freqX * Math.PI * 2 * 0.5) * amp * 0.4;
        const dy = Math.cos(px * freqX * Math.PI * 2 + 0.3) * amp * 0.6;
        const sx = Math.round((px + dx) * scW);
        const sy = Math.round((py + dy) * scH);
        const cx = Math.min(Math.max(sx, 0), clampedSrcW - 1);
        const cy = Math.min(Math.max(sy, 0), clampedSrcH - 1);
        const si = (cy * clampedSrcW + cx) * 4;
        const di = (py * w + px) * 4;
        dst.data[di] = src.data[si];
        dst.data[di + 1] = src.data[si + 1];
        dst.data[di + 2] = src.data[si + 2];
        dst.data[di + 3] = 255;
      }
    }

    const overlay = parseFloat(glassEl.getAttribute('overlay'));
    const overlayAlpha = Number.isNaN(overlay) ? 0.22 : overlay;
    const radius = parseRadius(glassEl, w, h);

    ctx.clearRect(0, 0, w, h);
    ctx.save();
    roundRectPath(ctx, 0, 0, w, h, radius);
    ctx.clip();
    ctx.putImageData(dst, 0, 0);
    ctx.fillStyle = 'rgba(255,255,255,' + overlayAlpha + ')';
    ctx.fill();
    ctx.restore();
  }

  class DreameGlassSource {
    constructor(el) {
      this.el = el;
      this.id = 'dgs-' + ++sourceId;
      this.canvas = document.createElement('canvas');
      this.canvas.className = 'dreame-glass-source__canvas';
      this.canvas.setAttribute('aria-hidden', 'true');
      if (!el.querySelector('.dreame-glass-source__canvas')) {
        el.prepend(this.canvas);
      }
      if (getComputedStyle(el).position === 'static') {
        el.style.position = 'relative';
      }
      this.glasses = new Set();
      this._bgImage = null;
      this._imageUrlPc = el.getAttribute('data-glass-image') || '';
      this._imageUrlMobile = el.getAttribute('data-glass-image-mobile') || '';
      this._activeImageUrl = '';
      this._raf = null;
      this._resizeObserver = new ResizeObserver(() => this.scheduleRedraw());
      this._resizeObserver.observe(el);
      this._mql = window.matchMedia('(max-width: 850px)');
      this._mqlHandler = () => this._loadBgImage();
      if (this._mql.addEventListener) {
        this._mql.addEventListener('change', this._mqlHandler);
      } else {
        this._mql.addListener(this._mqlHandler);
      }
      this._loadBgImage();
      sourceMap.set(el, this);
    }

    _resolveImageUrl() {
      if (this._mql.matches && this._imageUrlMobile) return this._imageUrlMobile;
      return this._imageUrlPc;
    }

    _loadBgImage() {
      const url = this._resolveImageUrl();
      if (url === this._activeImageUrl) return;
      this._activeImageUrl = url;
      if (!url) {
        this._bgImage = null;
        this.scheduleRedraw();
        return;
      }
      this._bgImage = new Image();
      this._bgImage.crossOrigin = 'anonymous';
      this._bgImage.onload = () => this.scheduleRedraw();
      this._bgImage.onerror = () => this.scheduleRedraw();
      this._bgImage.src = url;
    }

    register(glass) {
      this.glasses.add(glass);
      this.scheduleRedraw();
    }

    unregister(glass) {
      this.glasses.delete(glass);
    }

    scheduleRedraw() {
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this.redraw();
      });
    }

    redraw() {
      const rect = this.el.getBoundingClientRect();
      const w = Math.round(rect.width);
      const h = Math.round(rect.height);
      if (w <= 0 || h <= 0) return;

      this.canvas.width = w;
      this.canvas.height = h;
      this.canvas.style.width = w + 'px';
      this.canvas.style.height = h + 'px';

      const ctx = this.canvas.getContext('2d');
      const start = this.el.getAttribute('data-glass-gradient-start') || '#f6e9d0';
      const end = this.el.getAttribute('data-glass-gradient-end') || '#d2ecf8';
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, start);
      grad.addColorStop(1, end);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      if (this._bgImage && this._bgImage.complete && this._bgImage.naturalWidth > 0) {
        const img = this._bgImage;
        const iw = w;
        const ih = (img.naturalHeight / img.naturalWidth) * iw;
        const iy = h - ih;
        ctx.drawImage(img, 0, iy, iw, ih);
      }

      this.glasses.forEach((glass) => {
        if (glass.isConnected) glass._render();
      });
    }

    destroy() {
      this._resizeObserver.disconnect();
      if (this._mql) {
        if (this._mql.removeEventListener) {
          this._mql.removeEventListener('change', this._mqlHandler);
        } else {
          this._mql.removeListener(this._mqlHandler);
        }
      }
      if (this.canvas.parentNode === this.el) this.canvas.remove();
      sourceMap.delete(this.el);
    }
  }

  function getOrCreateSource(el) {
    if (!el) return null;
    if (sourceMap.has(el)) return sourceMap.get(el);
    return new DreameGlassSource(el);
  }

  class DreameGlass extends HTMLElement {
    constructor() {
      super();
      this._built = false;
      this._source = null;
      this._glassCanvas = null;
      this._io = null;
    }

    connectedCallback() {
      const variant = this.getAttribute('variant') || 'card';
      if (!this.getAttribute('data-variant')) this.setAttribute('data-variant', variant);

      if (!this._built) {
        const canvas = document.createElement('canvas');
        canvas.className = 'dreame-glass__canvas';
        canvas.setAttribute('aria-hidden', 'true');

        const ring = document.createElement('div');
        ring.className = 'dreame-glass__ring';
        if (this.getAttribute('ring') === 'false') ring.hidden = true;

        const ui = document.createElement('div');
        ui.className = 'dreame-glass__ui';
        while (this.firstChild) ui.appendChild(this.firstChild);

        this.append(canvas, ring, ui);
        this._glassCanvas = canvas;
        this._built = true;
      }

      this._bindSource();
      this._io = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) this._render();
        },
        { rootMargin: '80px' }
      );
      this._io.observe(this);

      this._ro = new ResizeObserver(() => {
        this._source?.scheduleRedraw();
      });
      this._ro.observe(this);
    }

    disconnectedCallback() {
      this._io?.disconnect();
      this._ro?.disconnect();
      if (this._source) this._source.unregister(this);
    }

    _bindSource() {
      const selector = this.getAttribute('source');
      const root = selector
        ? document.querySelector(selector)
        : this.closest('[data-glass-source]');

      if (!root) {
        this.classList.add('is-fallback');
        return;
      }

      this.classList.remove('is-fallback');
      this._source = getOrCreateSource(root);
      this._source.register(this);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => this._render());
      });
    }

    _render() {
      if (!this._source || !this._glassCanvas) return;
      renderGlassCanvas(this, this._source.canvas, this._source.el);
    }

    static refreshAll() {
      document.querySelectorAll('[data-glass-source]').forEach((el) => {
        const src = sourceMap.get(el);
        if (src) src.scheduleRedraw();
      });
    }
  }

  window.DreameGlass = DreameGlass;
  window.DreameGlassRefresh = DreameGlass.refreshAll;
  customElements.define('dreame-glass', DreameGlass);

  window.addEventListener(
    'resize',
    () => {
      clearTimeout(window.__dreameGlassResizeT);
      window.__dreameGlassResizeT = setTimeout(() => DreameGlass.refreshAll(), 120);
    },
    { passive: true }
  );

  document.addEventListener('shopify:section:load', () => {
    setTimeout(() => DreameGlass.refreshAll(), 150);
  });
})();
